import React from 'react';
import CurrencyFormat from 'react-currency-format';
import styled, { css } from 'styled-components';

import { colors } from '../../../core';
import Label from '../Label';
import TextInput, { TextInputProps } from '../TextInput';

type CurrencyInputValues = { floatValue: number; formattedValue: string; value: string };

export interface CurencyInputProps extends TextInputProps {
  id?: string;
  width?: string;
  label?: string;
  onChange: (values: CurrencyInputValues) => void;
  thousandSeparator?: boolean;
  prefix?: string;
  decimalScale?: number;
  allowNegative?: boolean;
}

const baseSmallLabelStyles = css`
  ${({ theme }) => `
    font-size: 12px;
    top: 0;
    color: ${theme.fontColor || colors.ash};
  `}
`;

const Container = styled.div<{ width: string }>`
  width: ${({ width }) => width};
`;

const AnimatedLabel = styled(Label)<{ hasEnteredText: boolean }>`
  position: absolute;
  top: 50%;
  left: 16px;
  transform: translateY(-50%);
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.5px;
  line-height: 2;
  transition: top 300ms ease-in-out, font-size 300ms ease-in-out, opacity 300ms ease-in-out;
  color: ${({ theme }) => theme.textInputColor || colors.ash};
  z-index: -1;
`;

const StyledCurrencyFormat = styled(CurrencyFormat)`
  -webkit-appearance: none;
  height: ${({ theme }) => theme.textInputHeight || '36px'};
  border: 0;
  background-color: transparent;
  color: ${colors.ash};
  display: block;
  font-family: ${({ theme }) => theme.fontFamily || `Quicksand, sans-serif`};
  font-size: ${({ theme }) => theme.fontSize || '14px'};
  font-weight: ${({ theme }) => theme.fontWeight || 600};
  padding: 8px 10px;
  width: 100%;

  ${({ theme, variant }) =>
    variant === 'primary' &&
    theme.textInputShowOnlyBottomBorder &&
    `
    border-top: 0;
    border-left: 0;
    border-right: 0;
    border-radius: 0;
`}

  ::placeholder {
    color: ${({ theme }) => theme.textInputPlaceholderColor || colors.smoke};
  }

  &:hover {
    box-shadow: ${({ theme }) =>
      theme.textInputHoverShadow || '0 2px 4px 0 rgba(217, 217, 217, 0.5)'};
  }

  &:active,
  &:focus {
    color: ${({ theme }) => theme.textInputFocusedColor || colors.charcoal};
    border-color: ${({ isError, theme }) =>
      isError ? theme.errorColor : theme.textInputFocusedBorderColor || colors.primary};
    outline: 0;
    & ~ ${AnimatedLabel} {
      ${baseSmallLabelStyles};
    }
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    & ~ ${AnimatedLabel} {
      ${baseSmallLabelStyles};
    }
  }

  &:disabled {
    background-color: ${({ theme }) =>
      !theme.textInputShowOnlyBottomBorder
        ? theme.textInputDisabledBackgroundColor || colors.pearl
        : 'transparent'};
    border-color: ${({ theme }) => theme.textInputDisabledBorderColor || colors.cloud};
    box-shadow: none;
    color: ${({ theme }) => theme.textInputDisabledColor || colors.smoke};
    cursor: default;
    -webkit-text-fill-color: ${({ theme }) => theme.textInputDisabledColor || colors.smoke};
    opacity: 1;
  }
`;

const CurrencyInput = ({
  width = '100%',
  label,
  onChange,
  decimalScale,
  allowNegative,
  errorMessage,
  ...inputProps
}: CurencyInputProps) => (
  <Container width={width}>
    <TextInput
      errorMessage={errorMessage}
      label={label}
      component={
        <StyledCurrencyFormat
          decimalScale={decimalScale}
          allowNegative={allowNegative}
          onValueChange={(values: CurrencyInputValues) => onChange(values)}
          {...inputProps}
        />
      }
    />
  </Container>
);

export default CurrencyInput;
